import { TooltipInline } from '@components/TooltipInline';
import * as D3Array from 'd3-array';
import * as D3Scale from 'd3-scale';
import * as D3Shape from 'd3-shape';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFragment } from 'react-relay';
import { baseDataChartFragment } from './BaseDataChart.relay';
import type { BaseDataChartPropsType } from './types';

const BaseDataChart: React.FC<BaseDataChartPropsType> = (props) => {
  const chart = useFragment(baseDataChartFragment, props.chart);

  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const updateDimension = useCallback((currentWidth: number, newWidth: number) => {
    if (newWidth === currentWidth) {
      return false;
    }

    setWidth(newWidth);
    setHeight(0.09 * newWidth);
    return true;
  }, []);

  const fitToParentSize = useCallback(() => {
    const nextTick = (callback) => setTimeout(callback, 0);
    const doDimensionUpdate = () => {
      if (!containerRef.current || !containerRef.current.parentElement) {
        setTimeout(doDimensionUpdate, 100);
        return;
      }
      updateDimension(width, containerRef.current.parentElement.offsetWidth);
    };
    nextTick(doDimensionUpdate);
  }, [width, updateDimension]);

  useEffect(() => {
    window.addEventListener('resize', fitToParentSize);
    fitToParentSize();

    return () => {
      window.removeEventListener('resize', fitToParentSize);
    };
  }, [fitToParentSize]);

  useEffect(() => {
    fitToParentSize();
  }, [chart, fitToParentSize]);

  const level = Number.parseInt(chart.level || '0', 10);
  const levelmax = Number.parseInt(chart.levelmax || '0', 10);
  const { leveltext1, leveltext2 } = chart;

  const rangeFull = level * 30;

  const dataFull = D3Array.range(rangeFull).map((i) => {
    return i % 30 ? { x: i / (levelmax * 30), y: 0.1 * i } : null;
  });

  const dataEmpty = D3Array.range(levelmax * 30 - rangeFull).map((i) => {
    return i % 30 ? { x: (i + rangeFull) / (levelmax * 30), y: (rangeFull + i) * 0.1 } : null;
  });

  const margin = { top: 0, right: 0, bottom: 0, left: 0 };
  const w = width - (margin.left + margin.right);
  const h = height - (margin.top + margin.bottom);

  const x = D3Scale.scaleLinear()
    .domain(D3Array.extent([...dataFull, ...dataEmpty], (d) => (d ? d.x : null)))
    .rangeRound([0, w]);

  const y = D3Scale.scaleLinear()
    .domain([0, D3Array.max([...dataFull, ...dataEmpty], (d) => (d ? d.y : null))])
    .range([h, 0]);

  const line = D3Shape.line<{ x: number; y: number }>()
    .defined((d) => d !== null)
    .x((d) => x(d.x))
    .y((d) => y(d.y));

  const area = D3Shape.area<{ x: number; y: number }>().defined(line.defined()).x(line.x()).y1(line.y()).y0(y(0));

  const transform = `translate(${margin.left},${margin.top})`;

  return (
    <div className="mt-[50px]" ref={containerRef}>
      <svg id="baseDataChart" width={width} height={height}>
        <title>baseDataChart</title>
        <g transform={transform}>
          <path d={line(dataFull) || undefined} strokeLinecap="round" fill="none" stroke="#668272" strokeWidth="0" />
          <path d={area(dataFull) || undefined} strokeLinecap="round" fill="#668272" />
          <path d={area(dataEmpty) || undefined} strokeLinecap="round" fill="#f3f3f3" />
        </g>
      </svg>
      <div>
        <div className="flex gap-2 items-center pt-2.5">
          <span className="text-2xl font-heading text-accent">{chart.text}</span>
          <TooltipInline text={chart.info} />
        </div>
        <small className="float-left">
          {leveltext1} {level} {leveltext2} {levelmax}
        </small>
      </div>
    </div>
  );
};

export { BaseDataChart };
